/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { Injectable } from '@angular/core'
import { map } from 'rxjs/operators'
import { Observable } from 'rxjs'

import { withFeedback } from '@app/core/interceptors/feedback-context'
import { ApiService } from '@shared/services/api.service'
import { PagesData } from '@shared/models/pages-data.model'
import { DeviceSensor, InputSensor, Sensor, SensorType, SensorDeviceInstance } from '@shared/models/sensor.model'
import { RequestType } from '../models/request-type.model'
import { FilterValues } from '../models/filter-values.model'
import { ExportFileTypes } from '../models/export-file-types.enum'
import { Dayjs } from 'dayjs'
import { ChartTemperatureByDevice } from '../models/chart-temperature-by-device.model'

@Injectable()
export class DeviceSensorsApiService {
  constructor(private apiService: ApiService) {}

  getDeviceSensor(
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof Sensor)[],
  ): Observable<PagesData<DeviceSensor>> {
    return this.apiService.getPageable<Sensor>(`/devices/sensors`, pageIndex, pageSize, sort, filterValues, fields)
  }

  getAllSensors(
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof Sensor)[],
  ): Observable<PagesData<Sensor>> {
    const inputFields = ['asset'].concat(fields || []) as (keyof Sensor)[]
    return this.apiService.getPageable<Sensor>(`/sensors`, pageIndex, pageSize, sort, filterValues, inputFields).pipe(
      map((data) => {
        const sensorsData = data.data.map((sensor) => ({ sensor, ...sensor }))
        return {
          ...data,
          data: sensorsData,
        }
      }),
    )
  }

  exportFile(columns: string[], sortValue: string, filterValues: FilterValues, type: ExportFileTypes): Observable<string> {
    return this.apiService.exportFile(`/sensors/${type}`, columns, sortValue, filterValues)
  }

  getAllSensorsList(filterValues?: FilterValues, fields?: (keyof Sensor)[]): Observable<Sensor[]> {
    return this.getAllSensors(1, 1000, null, filterValues, fields).pipe(map((data: PagesData<Sensor>) => data.data))
  }

  getDeviceSensorsList(id: number | string, filterValues?: FilterValues): Observable<DeviceSensor[]> {
    return this.apiService
      .getPageable<Sensor>(`/devices/${id}/sensors/history`, 1, 1000, null, filterValues)
      .pipe(map((pagesData) => pagesData.data))
  }

  getSensor(id: number, isDeviceSensor: boolean): Observable<Sensor> {
    const endPoint = isDeviceSensor ? `/devices/sensors/${id}` : `/sensors/${id}`

    return this.apiService.get(endPoint).pipe(map((resp) => resp['data']))
  }

  createSensor(deviceId: number, sensor: InputSensor): Observable<Sensor> {
    const endPoint = deviceId ? `/devices/${deviceId}/sensors` : '/sensors'

    return this.apiService.post(endPoint, sensor, { context: withFeedback() }).pipe(map((resp) => resp['data']))
  }

  editSensor(sensorId: number, sensor: InputSensor, isDeviceSensor: boolean): Observable<Sensor> {
    const endPoint = isDeviceSensor ? `/devices/sensors/${sensorId}` : `/sensors/${sensorId}`

    return this.apiService.patch(endPoint, sensor, { context: withFeedback() }).pipe(map((resp) => resp['data']))
  }

  deleteSensor(id: number, isDeviceSensor: boolean): Observable<void> {
    const endPoint = isDeviceSensor ? `/devices/sensors/${id}` : `/sensors/${id}`

    return this.apiService.delete(endPoint, { context: withFeedback() })
  }

  restoreSensor(id: number | string) {
    return this.apiService.patch(`/sensors/${id}/restore`, {}, { context: withFeedback() })
  }

  getSensorTypes(): Observable<SensorType[]> {
    return this.apiService.get('/sensors/types').pipe(map((resp) => resp['data']))
  }

  getSensorReportListByEntity<T>(
    pageIndex = 1,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    isByVehicle = false,
  ): Observable<PagesData<T>> {
    const url: string = isByVehicle ? '/vehicles/report/sensors/temp-and-humidity' : '/devices/sensors/report/temp-and-humidity'
    return this.apiService.getPageable<T>(url, pageIndex, pageSize, sort, filterValues)
  }

  // NOTE: POST used as fetch — sends a date-range / vehicleIds-array body too large for a query
  // string to RETRIEVE temperature chart data (the response IS the chart data). Do NOT opt in to
  // FormFeedbackInterceptor (withFeedback): would false-toast on every chart load.
  getChartTemperatureByVehicleData(
    startDate: Dayjs,
    endDate: Dayjs,
    minStartDate: Dayjs,
    maxEndDate: Dayjs,
    vehicleIds: number[],
    count: number,
  ): Observable<ChartTemperatureByDevice[]> {
    const url = '/devices/sensors/report/temp-and-humidity/vehicles/chart'
    return this.apiService.post(url, { startDate, endDate, minStartDate, maxEndDate, vehicleIds, count })
  }

  // NOTE: POST used as fetch — sends a date-range + sensorId body too large for a query string to
  // RETRIEVE temperature chart data (the response IS the chart data). Do NOT opt in to
  // FormFeedbackInterceptor (withFeedback): would false-toast on every chart load.
  getChartTemperatureBySensorData(
    startDate: Dayjs,
    endDate: Dayjs,
    minStartDate: Dayjs,
    maxEndDate: Dayjs,
    sensorId: number,
    count: number,
  ): Observable<ChartTemperatureByDevice> {
    const url = '/devices/sensors/report/temp-and-humidity/sensors/chart'
    return this.apiService.post(url, { startDate, endDate, minStartDate, maxEndDate, sensorId, count })
  }

  getSensorTemperatureFile(
    columns: string[],
    sortValue: string,
    filterValues: FilterValues,
    isByVehicle = false,
    type: ExportFileTypes,
  ): Observable<string> {
    const url: string = isByVehicle
      ? `/devices/sensors/report/temp-and-humidity/vehicles/${type}`
      : `/devices/sensors/report/temp-and-humidity/${type}`
    return this.apiService.exportFile(url, columns, sortValue, filterValues, RequestType.Post)
  }

  getSensorTemperatureListByEntity<T>(
    pageIndex = 1,
    pageSize: number,
    sort?: string,
    filterValues?: FilterValues,
    isByVehicle = false,
  ): Observable<PagesData<T>> {
    const url: string = isByVehicle
      ? '/devices/sensors/report/temp-and-humidity/vehicles/json'
      : '/devices/sensors/report/temp-and-humidity/json'
    const defaultSort = sort ? sort : 'occurredAt'
    return this.apiService.getPageable<T>(url, pageIndex, pageSize, defaultSort, filterValues, null, RequestType.Post)
  }

  getLinkedDevices(
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof SensorDeviceInstance)[],
  ): Observable<PagesData<SensorDeviceInstance>> {
    return this.apiService.getPageable<SensorDeviceInstance>(`/devices/sensors`, pageIndex, pageSize, sort, filterValues, fields)
  }
}
